import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Check, Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import * as C from "@/content/landing";
import { track } from "@/lib/analytics";
import { usePlans } from "@/hooks/usePlans";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { frasePreco } from "@/lib/preco";

// next=checkout carrega a intenção de assinar através do cadastro: sem conta
// não há workspace para vincular a assinatura, então a conta vem primeiro e o
// checkout é aberto logo depois, sem o usuário ter de procurar de novo.
const goRegister = (navigate, location, next) => () => {
  track("cta_click", { location });
  track("signup_start", { location });
  navigate(next ? `/registro?next=checkout&plan=${next}` : "/registro");
};

// ─── Hero ───────────────────────────────────────────────────────────────
export function Hero() {
  const navigate = useNavigate();
  const { precoDeEntrada } = usePlans();
  const h = C.hero;
  return (
    <section className="hero-gradient py-20 sm:py-24 px-6 relative overflow-hidden">
      <div className="max-w-3xl mx-auto relative z-10 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-8">
          <Shield size={14} className="text-blue-200" />
          <span className="text-blue-100 text-sm font-medium">{h.eyebrow}</span>
        </div>
        <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: "Cabinet Grotesk" }}>
          {h.h1}
        </h1>
        <p className="text-blue-50 text-lg sm:text-2xl font-medium mb-4">{h.subH1}</p>
        <p className="text-blue-100 text-base sm:text-lg leading-relaxed mb-9 max-w-2xl mx-auto">{h.subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            data-testid="hero-cta-btn"
            onClick={goRegister(navigate, "hero_primary")}
            className="bg-white text-[#003882] hover:bg-blue-50 font-bold text-lg h-14 px-8 w-full sm:w-auto"
          >
            {h.ctaPrimary}
            <ChevronRight size={20} />
          </Button>
          <Link to="/login" onClick={() => track("cta_click", { location: "hero_secondary" })} className="flex items-center justify-center text-white/85 hover:text-white font-medium text-base h-14 px-4">
            {h.ctaSecondary} →
          </Link>
        </div>
        <p className="text-blue-200 text-sm mt-5">
          {frasePreco(h.microcopyComPreco, precoDeEntrada(), h.microcopy)}
        </p>
      </div>
    </section>
  );
}

// ─── StatsBand / Tecnologias e parcerias ────────────────────────────────
export function StatsBand() {
  const stats = C.statsBand.filter((s) => s.value != null);
  if (!stats.length) return null;
  return (
    <section className="py-10 px-6 bg-[#F4F7FB] border-b border-[#D1DEEE]">
      <div className="max-w-5xl mx-auto">
        {stats.length > 0 && (
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6 mb-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold text-[#003882]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  +{s.value}
                </div>
                <div className="text-[#335480] text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4">
          {C.partners.map((p) => (
            <div
              key={p.src}
              title={p.alt}
              className={`group relative rounded-xl px-5 py-3 flex items-center justify-center h-16 w-32 shadow-sm hover:shadow-md transition-all ${
                p.dark
                  ? "bg-[#003882] border border-[#003882]"
                  : "bg-white border border-[#D1DEEE] hover:border-[#003882]/30"
              }`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                className={`max-h-9 max-w-full w-auto object-contain ${p.dark ? "brightness-0 invert" : ""} ${p.imgClass || ""}`}
              />
              {/* Tooltip com o nome */}
              <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#001533] px-2.5 py-1 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity z-20">
                {p.alt}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── HowItWorks ─────────────────────────────────────────────────────────
export function HowItWorks() {
  const navigate = useNavigate();
  const { title, steps, cta } = C.howItWorks;
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="scroll-reveal py-20 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#001533] text-center mb-14" style={{ fontFamily: "Cabinet Grotesk" }}>
          {title}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n} className="text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-[#003882] text-white flex items-center justify-center text-xl font-bold mb-5 mx-auto md:mx-0" style={{ fontFamily: "Cabinet Grotesk" }}>
                {s.n}
              </div>
              <h3 className="text-xl font-semibold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>{s.title}</h3>
              <p className="text-[#335480] text-base leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button onClick={goRegister(navigate, "how_it_works")} className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-12 px-8 text-base">
            {cta} <ChevronRight size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
}

// ─── Features ───────────────────────────────────────────────────────────
export function Features() {
  const ref = useScrollReveal();
  return (
    <section id="funcionalidades" ref={ref} className="scroll-reveal py-20 px-6 bg-[#F4F7FB]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Tudo que o advogado precisa
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {C.features.map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`} className="relative bg-white border border-[#D1DEEE] rounded-lg p-8 hover:border-[#003882] hover:-translate-y-1 transition-all duration-300">
              {f.badge && (
                <span className="absolute top-5 right-5 bg-[#e5edf5] text-[#003882] text-xs font-semibold px-2.5 py-1 rounded-full">
                  {f.badge}
                </span>
              )}
              <div className="w-12 h-12 bg-[#e5edf5] rounded-lg flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-[#003882]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-[#001533] mb-3" style={{ fontFamily: "Cabinet Grotesk" }}>{f.title}</h3>
              <p className="text-[#335480] text-base leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BeforeAfter ────────────────────────────────────────────────────────
export function BeforeAfter() {
  const { before, after } = C.beforeAfter;
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="scroll-reveal py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="rounded-xl p-8 border border-[#D1DEEE] bg-[#F4F7FB]">
          <h3 className="text-xl font-bold text-[#001533] mb-5" style={{ fontFamily: "Cabinet Grotesk" }}>{before.title}</h3>
          <ul className="space-y-3">
            {before.items.map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-[#335480] text-base">
                <X size={18} className="text-[#6685AD] flex-shrink-0 mt-0.5" /> {it}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl p-8 border border-[#003882]/20 bg-[#e5edf5]">
          <h3 className="text-xl font-bold text-[#001533] mb-5" style={{ fontFamily: "Cabinet Grotesk" }}>{after.title}</h3>
          <ul className="space-y-3">
            {after.items.map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-[#335480] text-base">
                <Check size={18} className="text-[#003882] flex-shrink-0 mt-0.5" /> {it}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Integrations ───────────────────────────────────────────────────────
export function Integrations() {
  const { title, subtitle, items } = C.integrations;
  return (
    <section className="py-16 px-6 bg-[#F4F7FB] border-y border-[#D1DEEE]">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#001533] mb-3" style={{ fontFamily: "Cabinet Grotesk" }}>{title}</h2>
        <p className="text-[#335480] text-base mb-8">{subtitle}</p>
        <div className="flex flex-wrap justify-center gap-4">
          {items.map((it) => (
            <span key={it} className="px-6 py-3 bg-white border border-[#D1DEEE] rounded-lg text-[#003882] font-bold text-lg">{it}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ───────────────────────────────────────────────────────
export function Testimonials() {
  // Sem depoimentos reais ainda — não renderiza (não fabricar). // TODO: conteúdo real
  if (!C.testimonials.length) return null;
  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
        {C.testimonials.map((t, i) => (
          <figure key={i} className="bg-[#F4F7FB] border border-[#D1DEEE] rounded-xl p-6">
            <blockquote className="text-[#335480] text-base leading-relaxed mb-4">“{t.quote}”</blockquote>
            <figcaption className="text-[#001533] font-semibold text-sm">
              {t.nome} <span className="text-[#335480] font-normal">· {t.oab_ou_cidade}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

// ─── FAQ ────────────────────────────────────────────────────────────────
export function Faq() {
  const { precoDeEntrada } = usePlans();
  const preco = precoDeEntrada();
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="scroll-reveal py-20 px-6 bg-[#F4F7FB]">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#001533] text-center mb-12" style={{ fontFamily: "Cabinet Grotesk" }}>
          Perguntas frequentes
        </h2>
        <Accordion
          type="single"
          collapsible
          className="w-full"
          onValueChange={(v) => { if (v) track("faq_open", { question_id: v }); }}
        >
          {C.faq.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-[#D1DEEE]">
              <AccordionTrigger className="text-left text-[#001533] text-base font-semibold hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#335480] text-base leading-relaxed">
                {frasePreco(item.aComPreco, preco, item.a)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ─── Pricing ────────────────────────────────────────────────────────────
export function Pricing() {
  const navigate = useNavigate();
  const { landingPricing } = usePlans();
  // Os valores vêm só da API. O conteúdo estático entra para nome, recursos e
  // textos de botão; preço ausente vira um traço, nunca um número de reserva.
  const daApi = landingPricing();
  const pricing = daApi || C.pricing;
  const { free, pro } = pricing;
  const ref = useScrollReveal();
  return (
    <section id="planos" ref={ref} className="scroll-reveal py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Simples e transparente
          </h2>
        </div>
        <div className={`grid gap-8 ${free ? "md:grid-cols-2" : "max-w-md mx-auto"}`}>
          {/* FREE — só renderiza se ativo */}
          {free && (
          <div className="border border-[#D1DEEE] rounded-xl p-8">
            <h3 className="text-2xl font-bold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>{free.name}</h3>
            <div className="text-4xl font-bold text-[#003882] mb-6">
              {free.price || <span className="text-[#D1DEEE]">—</span>}{" "}
              <span className="text-lg text-[#335480] font-normal">{free.period}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {free.features.map((it, i) => (
                <li key={i} className="flex items-center gap-3 text-[#335480] text-base">
                  <Check size={16} className="text-[#003882] flex-shrink-0" /> {it}
                </li>
              ))}
            </ul>
            <Button data-testid="free-plan-btn" onClick={goRegister(navigate, "pricing_free")} variant="outline" className="w-full h-12 border-[#003882] text-[#003882] hover:bg-[#e5edf5] font-semibold text-base">
              {free.cta}
            </Button>
            <p className="text-center text-[#335480] text-xs mt-3">{free.microcopy}</p>
          </div>
          )}
          {/* PRO */}
          <div className="bg-[#003882] rounded-xl p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">RECOMENDADO</div>
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>{pro.name}</h3>
            <div className="mb-1">
              {pro.oldPrice && <span className="text-lg line-through text-blue-300/70 mr-2">{pro.oldPrice}</span>}
              <span className="text-4xl font-bold text-white">
                {pro.price || <span className="text-blue-300/50">—</span>}
              </span>
              <span className="text-blue-200 text-lg">{pro.period}</span>
            </div>
            {pro.annualNote && <p className="text-blue-200 text-sm mb-6">{pro.annualNote}</p>}
            <ul className="space-y-3 mb-8">
              {pro.features.map((it, i) => (
                <li key={i} className="flex items-center gap-3 text-blue-100 text-base">
                  <Check size={16} className="text-blue-300 flex-shrink-0" /> {it}
                </li>
              ))}
            </ul>
            <Button data-testid="pro-plan-btn" onClick={goRegister(navigate, "pricing_pro", "pro_monthly")} className="w-full h-12 bg-white text-[#003882] hover:bg-blue-50 font-bold text-base">
              {pro.cta}
            </Button>
            <p className="text-center text-blue-200 text-xs mt-3">{pro.microcopy}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FinalCta ───────────────────────────────────────────────────────────
export function FinalCta() {
  const navigate = useNavigate();
  const { precoDeEntrada } = usePlans();
  const f = C.finalCta;
  return (
    <section className="hero-gradient py-20 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8" style={{ fontFamily: "Cabinet Grotesk" }}>{f.title}</h2>
        <Button onClick={goRegister(navigate, "final_cta")} className="bg-white text-[#003882] hover:bg-blue-50 font-bold text-lg h-14 px-8">
          {frasePreco(f.ctaComPreco, precoDeEntrada(), f.cta)} <ChevronRight size={20} />
        </Button>
        <p className="text-blue-200 text-sm mt-5">{f.microcopy}</p>
      </div>
    </section>
  );
}
